// src/utils/waiverGate.js
// Shared waiver acceptance helpers used by every purchase/signup flow that
// gates on a clubDocuments waiver. Single source of truth for:
//   - Checking whether a user has a current (non-expired) acceptance
//   - Building the acceptance entry shape that gets written to
//     users/{uid}.documentAcceptances[docId]
//
// Acceptance shape on disk:
//   users/{uid}.documentAcceptances[docId] = {
//     acceptedAt:    Timestamp     server time of acceptance
//     docName:       string        snapshot of doc.name
//     actionType:    string        'accept' | 'signature' | etc
//     docVersion:    Timestamp     snapshot of doc.updatedAt
//     expirationDays: number       snapshot of doc.expirationDays at acceptance time
//     signature:     string        present only for actionType === 'signature'
//   }
//
// Expiration policy: the gate check reads docRecord.expirationDays LIVE from
// the current doc, not from the acceptance snapshot. So shortening the
// expiration in admin invalidates older acceptances immediately. The snapshot
// is informational only.

import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { canonicalUid } from './resolveMember';

// Returns true if the user has an acceptance entry for `docRecord` that is
// still within the document's current expiration window. Returns false on
// any error or missing data — safe-fail closed.
export async function hasValidWaiverAcceptance(uid, docRecord) {
  if (!uid || !docRecord?.id) return false;
  try {
    // Resolved here rather than at each call site: every caller passes the
    // signed-in uid, and for a merged login that doc holds only a pointer — so
    // acceptances were never found and the waiver was asked for again on every
    // visit. createMemberBooking reads the canonical doc for the same field, so
    // this is also what keeps the two answers the same.
    const snap = await getDoc(doc(db, 'users', await canonicalUid(db, uid)));
    if (!snap.exists()) return false;
    const acc = snap.data().documentAcceptances || {};
    const entry = acc[docRecord.id];
    if (!entry || !entry.acceptedAt) return false;
    // Never-expires path: any acceptance counts.
    if (!docRecord.expirationDays || docRecord.expirationDays <= 0) return true;
    // Resolve accepted-at to milliseconds. Timestamp objects vary in shape
    // depending on whether the doc was just written or read from cache.
    const acceptedMs =
      entry.acceptedAt?.toMillis?.() ??
      (entry.acceptedAt?.seconds ? entry.acceptedAt.seconds * 1000 : null);
    if (!acceptedMs) return false;
    const ageDays = (Date.now() - acceptedMs) / (1000 * 60 * 60 * 24);
    return ageDays < docRecord.expirationDays;
  } catch (e) {
    console.warn('hasValidWaiverAcceptance failed:', e);
    return false;
  }
}

// Builds the entry that gets stored under users/{uid}.documentAcceptances[docId].
// Caller is responsible for actually writing this via updateDoc with a dotted
// path: { [`documentAcceptances.${docRecord.id}`]: entry }.
export function buildAcceptanceEntry(docRecord, { signature } = {}) {
  const entry = {
    acceptedAt: serverTimestamp(),
    docName: docRecord.name || '',
    actionType: docRecord.actionType || 'accept',
    docVersion: docRecord.updatedAt || null,
    expirationDays: docRecord.expirationDays || 0,
  };
  if (docRecord.actionType === 'signature' && signature) {
    entry.signature = String(signature).trim();
  }
  return entry;
}
